import { Component, type ErrorInfo, type ReactNode } from 'react';

import { ConfigErrorScreen } from './ConfigErrorScreen.js';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Top-level React error boundary. If anything in the render tree throws —
 * a malformed tile, a missing prop, a runtime invariant — we drop to the
 * config-error screen with a "Tap to retry" that hard-reloads the page.
 *
 * Per docs: "Fail to Core, never to crash. ... A bricked kiosk is a support
 * disaster." Reload is a blunt instrument but the right default for a kiosk:
 * it returns the launcher to a known state and re-runs the engine boot.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[mobilauncher] uncaught render error:', error, info);
  }

  reset = (): void => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    } else {
      this.setState({ error: null });
    }
  };

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <ConfigErrorScreen
          variant="crash"
          details={{ message: this.state.error.message, stack: this.state.error.stack }}
          onRetry={this.reset}
        />
      );
    }
    return this.props.children;
  }
}
